import { RefObject } from 'react';
import { Combobox, ComboboxRef, GetItemsProps } from 'medere-ui-framework';
import { getPaymentMethodsForCombo } from '../../services/SupplierController';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { NO_OPEN_CASH_REGISTER_MESSAGE, PaymentOrigin } from '../../hooks/usePaymentOrigin';
import { formatMoney } from '../../utils/money';
import FieldError from '../FieldError/FieldError';

export type PaymentOriginField = 'paymentMethod' | 'cashRegister' | 'financialAccount';

interface PaymentOriginFieldsProps {
    /** Resultado de `usePaymentOrigin`. */
    origin: PaymentOrigin;
    paymentMethod: ComboDataDTO | null;
    onPaymentMethodChange: (value: ComboDataDTO | null) => void;
    /** Marcadores por campo (DS §3.8.1). */
    errors?: Partial<Record<PaymentOriginField, string>>;
    /** Se llama al cambiar un campo: el formulario borra su marcador. */
    onFieldChange?: (field: PaymentOriginField) => void;
    /** Aviso sobre la cuenta elegida que no viene de la validación (ej.: saldo insuficiente para el monto). */
    accountWarning?: string | null;
    disabled?: boolean;
    /** Prefijo de los ids de las leyendas de error. */
    idPrefix: string;
    /** Contenedor de cada campo, para que el formulario controle la grilla. */
    fieldClassName?: string;
    refs?: Partial<Record<PaymentOriginField, RefObject<ComboboxRef>>>;
}

/**
 * Origen de un pago a un profesional, igual en la rendición y en la liquidación: medio de pago, caja de origen
 * (solo la que el usuario tiene abierta) y cuenta financiera con su saldo disponible.
 * Si el sitio no maneja cajas, solo pide el medio de pago.
 */
export default function PaymentOriginFields({
    origin,
    paymentMethod,
    onPaymentMethodChange,
    errors,
    onFieldChange,
    accountWarning,
    disabled,
    idPrefix,
    fieldClassName,
    refs,
}: PaymentOriginFieldsProps) {
    const accountMessage = errors?.financialAccount ?? accountWarning ?? null;

    function balanceText(): string | null {
        if (origin.loadingBalance) return 'Consultando saldo disponible…';
        if (origin.balance == null) return 'No se pudo consultar el saldo. Volvé a seleccionar la cuenta.';
        return `Saldo disponible: ${formatMoney(origin.balance)}`;
    }

    return (
        <>
            {/* El label de Combobox es texto plano: el asterisco va en el texto */}
            <div className={fieldClassName}>
                <Combobox
                    ref={refs?.paymentMethod}
                    label="Medio de pago *"
                    getItems={(p: GetItemsProps) => getPaymentMethodsForCombo(p)}
                    optionLabel="name"
                    value={paymentMethod}
                    setValue={(value: ComboDataDTO | null) => {
                        onPaymentMethodChange(value);
                        onFieldChange?.('paymentMethod');
                    }}
                    placeholder="Seleccionar"
                    error={!!errors?.paymentMethod}
                    disable={disabled}
                />
                <FieldError id={`${idPrefix}-payment-method-error`} message={errors?.paymentMethod} />
            </div>

            {origin.requiresPaymentOrigin && (
                <>
                    {/* Solo la caja que el usuario tiene abierta: el pago no puede salir de otra */}
                    <div className={fieldClassName}>
                        <Combobox
                            ref={refs?.cashRegister}
                            label="Caja de origen *"
                            list={origin.cashRegisterOptions}
                            optionLabel="displayDescription"
                            value={origin.cashRegister}
                            setValue={(value: typeof origin.cashRegister) => {
                                origin.setCashRegister(value);
                                onFieldChange?.('cashRegister');
                            }}
                            placeholder={origin.missingOpenCashRegister ? 'Sin caja abierta' : 'Seleccionar'}
                            error={!!errors?.cashRegister}
                            disable={disabled || origin.missingOpenCashRegister}
                        />
                        <FieldError id={`${idPrefix}-cash-register-error`} message={errors?.cashRegister} />
                        {origin.missingOpenCashRegister && <p className="md-help">{NO_OPEN_CASH_REGISTER_MESSAGE}</p>}
                    </div>

                    <div className={fieldClassName}>
                        <Combobox
                            ref={refs?.financialAccount}
                            label="Cuenta financiera de origen *"
                            list={origin.financialAccountOptions}
                            optionLabel="financialAccountName"
                            value={origin.financialAccount}
                            setValue={(value: typeof origin.financialAccount) => {
                                origin.setFinancialAccount(value);
                                onFieldChange?.('financialAccount');
                            }}
                            placeholder={origin.loadingFinancialAccounts ? 'Cargando…' : 'Seleccionar'}
                            error={!!accountMessage}
                            disable={disabled || origin.loadingFinancialAccounts || !origin.cashRegister || !paymentMethod}
                        />
                        <FieldError id={`${idPrefix}-financial-account-error`} message={accountMessage} />
                        {!accountMessage && !origin.missingOpenCashRegister && (!origin.cashRegister || !paymentMethod) && (
                            <p className="md-help">Se habilita al elegir el medio de pago y la caja.</p>
                        )}
                        {!accountMessage && origin.financialAccount && (
                            <p className="md-help" aria-live="polite">{balanceText()}</p>
                        )}
                    </div>
                </>
            )}
        </>
    );
}

